/**
 * 客服窗口里的截图：用浏览器的屏幕共享接口截取当前标签页的一帧，再按客户框选的区域裁剪成图片。
 *
 * - 每次截图浏览器都会弹“共享此标签页”的确认，客户点【共享】后只取一帧就停止共享，不录制。
 * - getDisplayMedia 必须在点击当下调用（浏览器要求用户手势）；iframe 需宿主加 allow="display-capture"。
 * - 截图前宿主已把客服窗口隐藏，等一小会儿让页面和浏览器的“正在共享”提示条稳定后再取帧。
 */

/** 浏览器支持屏幕共享截图（Chrome/Edge 等）；不支持时不显示截图按钮。 */
export function screenCaptureSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/** 截图失败的原因：cancelled 客户在浏览器确认框里点了取消（或浏览器拒绝），failed 其他错误。 */
export class ScreenCaptureError extends Error {
  readonly reason: 'cancelled' | 'failed';

  constructor(reason: 'cancelled' | 'failed', message: string) {
    super(message);
    this.name = 'ScreenCaptureError';
    this.reason = reason;
  }
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** 截取当前标签页的一帧，返回画布（像素为实际分辨率）。 */
export async function captureCurrentTab(settleMs = 400): Promise<HTMLCanvasElement> {
  if (!screenCaptureSupported()) throw new ScreenCaptureError('failed', '浏览器不支持截图');
  let stream: MediaStream;
  try {
    // preferCurrentTab 等是 Chrome 扩展的选项，类型定义里没有。
    stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      video: { displaySurface: 'browser' },
    } as DisplayMediaStreamOptions);
  } catch (error) {
    const name = error instanceof DOMException ? error.name : '';
    throw new ScreenCaptureError(name === 'NotAllowedError' || name === 'AbortError' ? 'cancelled' : 'failed', String(error));
  }
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new ScreenCaptureError('failed', '没有可用的画面');
    await delay(settleMs);
    return await grabFrame(track, stream);
  } finally {
    stream.getTracks().forEach((item) => item.stop());
  }
}

interface ImageCaptureLike {
  grabFrame: () => Promise<ImageBitmap>;
}

async function grabFrame(track: MediaStreamTrack, stream: MediaStream): Promise<HTMLCanvasElement> {
  const ImageCaptureCtor = (globalThis as { ImageCapture?: new (track: MediaStreamTrack) => ImageCaptureLike }).ImageCapture;
  if (ImageCaptureCtor) {
    try {
      const bitmap = await new ImageCaptureCtor(track).grabFrame();
      const canvas = drawToCanvas(bitmap, bitmap.width, bitmap.height);
      bitmap.close();
      return canvas;
    } catch {
      // 个别浏览器 grabFrame 失败，改用 video 取帧。
    }
  }
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  try {
    await video.play();
    if (video.videoWidth === 0) {
      await new Promise<void>((resolve) => video.addEventListener('loadeddata', () => resolve(), { once: true }));
    }
    await delay(100);
    if (video.videoWidth === 0 || video.videoHeight === 0) throw new ScreenCaptureError('failed', '没有取到画面');
    return drawToCanvas(video, video.videoWidth, video.videoHeight);
  } finally {
    video.pause();
    video.srcObject = null;
  }
}

function drawToCanvas(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new ScreenCaptureError('failed', '无法绘制截图');
  context.drawImage(source, 0, 0, width, height);
  return canvas;
}

/** 裁剪区域（原图像素）。 */
export interface CropRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

/** 智能客服识图单张上限 5MB：PNG 超过 4.5MB 时改存 JPEG。 */
const PNG_LIMIT = 4.5 * 1024 * 1024;

/** 按区域裁剪成图片文件；rect 为空时取整张。 */
export async function cropToFile(source: HTMLCanvasElement, rect: CropRect | null, now = new Date()): Promise<File> {
  const area = clampRect(rect ?? { height: source.height, width: source.width, x: 0, y: 0 }, source.width, source.height);
  const canvas = document.createElement('canvas');
  canvas.width = area.width;
  canvas.height = area.height;
  const context = canvas.getContext('2d');
  if (!context) throw new ScreenCaptureError('failed', '无法裁剪截图');
  context.drawImage(source, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  const stamp = [now.getHours(), now.getMinutes(), now.getSeconds()].map((n) => String(n).padStart(2, '0')).join('');
  let blob = await toBlob(canvas, 'image/png');
  if (blob.size <= PNG_LIMIT) return new File([blob], `截图-${stamp}.png`, { type: 'image/png' });
  blob = await toBlob(canvas, 'image/jpeg', 0.9);
  return new File([blob], `截图-${stamp}.jpg`, { type: 'image/jpeg' });
}

export function clampRect(rect: CropRect, maxWidth: number, maxHeight: number): CropRect {
  const x = Math.min(Math.max(0, Math.round(rect.x)), maxWidth - 1);
  const y = Math.min(Math.max(0, Math.round(rect.y)), maxHeight - 1);
  const width = Math.max(1, Math.min(Math.round(rect.width), maxWidth - x));
  const height = Math.max(1, Math.min(Math.round(rect.height), maxHeight - y));
  return { height, width, x, y };
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new ScreenCaptureError('failed', '截图编码失败'))), type, quality);
  });
}
